import { useMemo, useState } from 'react';

import Icon from './Icon.jsx';
import { CHAPTERS, CHAPTERS_BY_ID } from '../data/story.js';
import { COASTLINE, MAP_BOUNDS } from '../data/mapOutline.js';
import { CERTAINTY, MAP_LABELS, PLACES } from '../data/places.js';

/**
 * The story from above.
 *
 * Same twenty-eight chapters as the trail, plotted where they happened, with
 * the route drawn between them in the order you walk them. The point it makes
 * that a list cannot: how far apart these places actually are. Egypt to Canaan
 * is a fortnight's walk and forty years in the text, and you only feel that
 * when you can see both ends at once.
 *
 * Locked chapters are on the map but unnamed — the shape of the journey ahead
 * without giving away where it goes.
 */

const WIDTH = 1000;
const HEIGHT = 620;

/** Great-circle distance in miles, for "how far was that, really". */
function milesBetween([lon1, lat1], [lon2, lat2]) {
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return Math.round(3959 * 2 * Math.asin(Math.sqrt(a)));
}

/**
 * Equirectangular, with the longitudes squeezed by the cosine of the middle
 * latitude. At 33° north a degree of longitude is about five sixths of a
 * degree of latitude, and ignoring that stretches the Mediterranean into
 * something nobody would recognise.
 */
function projector() {
  const midLat = ((MAP_BOUNDS.north + MAP_BOUNDS.south) / 2) * (Math.PI / 180);
  const squeeze = Math.cos(midLat);

  const spanLon = (MAP_BOUNDS.east - MAP_BOUNDS.west) * squeeze;
  const spanLat = MAP_BOUNDS.north - MAP_BOUNDS.south;

  // Fit the window into the viewBox without distorting either axis.
  const scale = Math.min(WIDTH / spanLon, HEIGHT / spanLat);
  const offsetX = (WIDTH - spanLon * scale) / 2;
  const offsetY = (HEIGHT - spanLat * scale) / 2;

  return ([lon, lat]) => [
    offsetX + (lon - MAP_BOUNDS.west) * squeeze * scale,
    offsetY + (MAP_BOUNDS.north - lat) * scale,
  ];
}

export default function StoryMap({ story, isPremium, onOpenChapter, onUpgrade }) {
  const [selected, setSelected] = useState(null);
  const project = useMemo(projector, []);

  const coast = useMemo(
    () =>
      COASTLINE.map((line) =>
        line.map((point) => project(point).map((n) => Math.round(n * 10) / 10).join(',')).join(' '),
      ),
    [project],
  );

  // The chapters in trail order, each with where it happened and whether it is
  // reachable yet. Sharing `story` with the trail is what keeps the two views
  // showing the same progress rather than two different ideas of it.
  const stops = useMemo(() => {
    let reachedLock = false;
    const placed = PLACES.map((place) => {
      const chapter = CHAPTERS_BY_ID[place.chapterId];
      const order = CHAPTERS.findIndex((c) => c.id === place.chapterId);
      const done = !!story[place.chapterId]?.done;
      const locked = !isPremium && order >= 4;
      const current = !done && !locked && !reachedLock;
      if (current) reachedLock = true;
      return { place, chapter, order, done, locked, current, xy: project(place.at) };
    });

    // Fan out anything sitting on top of something else.
    //
    // Five chapters happen in Jerusalem, at the same coordinates, because they
    // happened in the same city. Drawn honestly that is one dot with four
    // chapters hidden underneath it and only the last one tappable. Spreading a
    // cluster around a small ring is what every map does with this, and it
    // costs a few pixels of precision to show four chapters that were
    // otherwise invisible.
    const clusters = new Map();
    for (const stop of placed) {
      const key = `${Math.round(stop.xy[0] / 14)}:${Math.round(stop.xy[1] / 14)}`;
      if (!clusters.has(key)) clusters.set(key, []);
      clusters.get(key).push(stop);
    }

    for (const group of clusters.values()) {
      if (group.length < 2) continue;
      const radius = 9 + group.length * 2.2;
      const [cx, cy] = group[0].xy;
      group.forEach((stop, i) => {
        // Start at the top and go clockwise, so the order round the ring is the
        // order of the chapters.
        const angle = (i / group.length) * Math.PI * 2 - Math.PI / 2;
        stop.xy = [cx + Math.cos(angle) * radius, cy + Math.sin(angle) * radius];
        stop.clustered = true;
      });
      group.anchor = [cx, cy];
    }

    return placed;
  }, [story, isPremium, project]);

  /**
   * How big each tap target can be without stealing its neighbour's.
   *
   * A fixed radius is wrong on a map: pins are inches apart in the Levant and
   * a screen's width apart between Rome and Babylon. Half the distance to the
   * nearest other pin is the largest circle that cannot overlap one.
   */
  const hitRadii = useMemo(
    () =>
      stops.map((stop, i) => {
        let nearest = Infinity;
        for (let j = 0; j < stops.length; j++) {
          if (i === j) continue;
          const distance = Math.hypot(stops[i].xy[0] - stops[j].xy[0], stops[i].xy[1] - stops[j].xy[1]);
          if (distance < nearest) nearest = distance;
        }
        return Math.max(7, Math.min(22, nearest / 2));
      }),
    [stops],
  );

  const walked = stops.filter((s) => s.done);
  const chosen = selected ? stops.find((s) => s.place.chapterId === selected) : null;

  const previousOf = (stop) => {
    const index = stops.findIndex((s) => s.place.chapterId === stop.place.chapterId);
    return index > 0 ? stops[index - 1] : null;
  };

  return (
    <div className="map">
      <p className="map__intro">
        Every chapter, plotted where it happened. The route is drawn as far as
        you have walked it — and the distances are real, which is the point:
        Egypt to Canaan is a fortnight on foot and forty years in the text.
      </p>

      <svg
        className="map__plate"
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        role="img"
        aria-label="A map of where the story happened"
        onClick={(event) => {
          // Tapping the sea closes the card rather than leaving it stuck open.
          if (event.target === event.currentTarget) setSelected(null);
        }}
      >
        <defs>
          <radialGradient id="map-sea" cx="50%" cy="40%" r="75%">
            <stop offset="0%" stopColor="#101a2c" />
            <stop offset="100%" stopColor="#080c16" />
          </radialGradient>
          <linearGradient id="map-route" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#c9a84c" />
            <stop offset="100%" stopColor="#e8d59a" />
          </linearGradient>
        </defs>

        <rect width={WIDTH} height={HEIGHT} fill="url(#map-sea)" />

        {/* Graticule — every five degrees, faint. It is what makes the map read
            as a chart rather than as a drawing. */}
        <g className="map__grid">
          {Array.from({ length: 9 }, (_, i) => MAP_BOUNDS.west + i * 5)
            .filter((lon) => lon <= MAP_BOUNDS.east)
            .map((lon) => {
              const [x] = project([lon, MAP_BOUNDS.north]);
              return <line key={`v${lon}`} x1={x} y1={0} x2={x} y2={HEIGHT} />;
            })}
          {Array.from({ length: 6 }, (_, i) => MAP_BOUNDS.south + i * 5)
            .filter((lat) => lat <= MAP_BOUNDS.north)
            .map((lat) => {
              const [, y] = project([MAP_BOUNDS.west, lat]);
              return <line key={`h${lat}`} x1={0} y1={y} x2={WIDTH} y2={y} />;
            })}
        </g>

        {/* Land, filled. The first version of this map stroked the coast and
            filled nothing, so sea and land were the same colour and the whole
            thing read as an abstract squiggle. */}
        <g className="map__land">
          {coast.map((points, i) => (
            <polygon key={i} points={points} />
          ))}
        </g>

        {/* And the names. Without these it is impossible to tell which sea you
            are looking at, or that any of it is the Middle East. */}
        <g className="map__labels" aria-hidden="true">
          {MAP_LABELS.map((label) => {
            const [x, y] = project(label.at);
            return (
              <text
                key={label.text}
                x={x}
                y={y}
                className={`map__label map__label--${label.size}`}
                transform={label.rotate ? `rotate(${label.rotate} ${x} ${y})` : undefined}
              >
                {label.text}
              </text>
            );
          })}
        </g>

        {/* The route, drawn only as far as you have walked. */}
        <g className="map__route">
          {stops.map((stop, i) => {
            if (i === 0 || !stop.place.journey) return null;
            const previous = stops[i - 1];
            if (!stop.done && !previous.done) return null;
            const [x1, y1] = previous.xy;
            const [x2, y2] = stop.xy;
            // Bow each leg slightly, so overlapping journeys stay distinct and
            // the whole thing reads as travel rather than as wiring.
            const bow = 0.16 * Math.hypot(x2 - x1, y2 - y1);
            const mx = (x1 + x2) / 2 + (y2 - y1) * 0.14;
            const my = (y1 + y2) / 2 - (x2 - x1) * 0.14;
            return (
              <path
                key={stop.place.chapterId}
                d={`M ${x1} ${y1} Q ${mx} ${my} ${x2} ${y2}`}
                className={stop.done ? 'is-walked' : 'is-next'}
                style={{ strokeDasharray: bow ? undefined : '4 6' }}
              />
            );
          })}
        </g>

        {/* The pulse on the current stop lives in its own layer, outside the
            clickable groups. An animation inside the hit target makes the
            target's own geometry move, which defeats both pointer hit-testing
            and anyone trying to tap it. */}
        <g className="map__pulses" aria-hidden="true">
          {stops
            .filter((stop) => stop.current)
            .map((stop) => (
              <circle
                key={stop.place.chapterId}
                className="map__ping"
                cx={stop.xy[0]}
                cy={stop.xy[1]}
                r="8"
              />
            ))}
        </g>

        <g className="map__stops">
          {stops.map((stop, index) => {
            const [x, y] = stop.xy;
            const kind = CERTAINTY[stop.place.certainty].pin;
            const state = stop.done
              ? 'is-done'
              : stop.locked
                ? 'is-locked'
                : stop.current
                  ? 'is-current'
                  : '';
            return (
              <g
                key={stop.place.chapterId}
                className={`map__stop map__stop--${kind} ${state}`}
                transform={`translate(${x} ${y})`}
                onClick={() => setSelected(stop.place.chapterId)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setSelected(stop.place.chapterId);
                  }
                }}
                aria-label={
                  stop.locked ? 'A locked chapter' : `${stop.place.name} — ${stop.chapter.title}`
                }
              >
                {/* A region or an unknown is a soft disc, not a point. Drawing
                    a pin where nobody knows the place would be a lie told in
                    graphics. */}
                {(kind === 'area' || kind === 'ghost') && <circle className="map__haze" r="26" />}
                <circle className="map__dot" r={stop.current ? 8 : 6} />
                {/* The pin is 6px because that is the right size to look at.
                    This is the size to hit: on a phone the visible dot is well
                    under the 44px a fingertip needs. */}
                <circle className="map__hit" r={hitRadii[index]} />

                {/* Name the ones you have reached, and the one you are on.
                    Naming all twenty-eight would be unreadable; naming none was
                    the problem. */}
                {(stop.done || stop.current) && !stop.locked ? (
                  <text className="map__pin-name" x="0" y={-12}>
                    {stop.place.name}
                  </text>
                ) : null}
              </g>
            );
          })}
        </g>
      </svg>

      {/* Naming the places on the plate itself would need collision handling
          the map does not earn; a card on tap is clearer and works on a phone. */}
      {chosen ? (
        <div className="map__card" role="dialog" aria-live="polite">
          {chosen.locked ? (
            <>
              <p className="map__card-eyebrow">Chapter {chosen.order + 1}</p>
              <h3>Somewhere ahead</h3>
              <p className="map__card-note">
                The route goes on. Unlock the whole story to see where.
              </p>
              <button type="button" className="btn btn--primary btn--sm" onClick={onUpgrade}>
                Unlock the story
              </button>
            </>
          ) : (
            <>
              <p className="map__card-eyebrow">
                Chapter {chosen.order + 1} · {CERTAINTY[chosen.place.certainty].label}
              </p>
              <h3>{chosen.place.name}</h3>
              <p className="map__card-note">{chosen.place.note}</p>

              {/* How far it is from the chapter before. This is the number the
                  map exists to make visible, so it is stated rather than left
                  to be estimated off the graticule. */}
              {(() => {
                const from = previousOf(chosen);
                if (!from || from.place.chapterId === chosen.place.chapterId) return null;
                const miles = milesBetween(from.place.at, chosen.place.at);
                if (miles < 5) {
                  return (
                    <p className="map__card-distance">
                      Barely a mile from {from.place.name} — the story keeps
                      returning to the same few streets.
                    </p>
                  );
                }
                return (
                  <p className="map__card-distance">
                    <strong>{miles} miles</strong> from {from.place.name}
                    <span> · about {Math.max(1, Math.round(miles / 18))} days on foot</span>
                  </p>
                );
              })()}
              <button
                type="button"
                className="btn btn--primary btn--sm"
                onClick={() => onOpenChapter(chosen.place.chapterId)}
              >
                {chosen.done ? 'Read it again' : 'Read this chapter'}
                <Icon name="arrow" size={14} />
              </button>
            </>
          )}
          <button
            type="button"
            className="map__card-close"
            onClick={() => setSelected(null)}
            aria-label="Close"
          >
            <Icon name="close" size={13} />
          </button>
        </div>
      ) : (
        <p className="map__hint">
          {walked.length
            ? `${walked.length} of ${stops.length} places reached. Tap a point to see where you are.`
            : 'Tap any point on the map to see what happened there.'}
        </p>
      )}

      <ul className="map__key">
        {Object.entries(CERTAINTY).map(([key, { label, pin }]) => (
          <li key={key}>
            <span className={`map__key-pin map__key-pin--${pin}`} aria-hidden="true" />
            {label}
          </li>
        ))}
      </ul>
    </div>
  );
}
